import { useQuery } from "@tanstack/react-query"
import { MessageSquare, X } from "lucide-react"
import * as React from "react"

import { EmptyState, SkeletonText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { IconButton } from "@/components/ui/icon-button"
import { Textarea } from "@/components/ui/textarea"
import { formatRelative } from "@/lib/format-date"
import { canRemarkOnEvalRun, type Role } from "@/lib/roles"

import { evalRemarksQuery, usePostRemarkMutation } from "../api"
import {
  OUTCOME_LABEL,
  OUTCOME_TONE,
  type EvalFinding,
  type EvalMark,
  type Remark,
  type RemarkTargetKind,
} from "../eval-runs-data"

export interface RemarkTarget {
  kind: RemarkTargetKind
  ref: string
  title: string
}

/** One thread of remarks — on a mark, a finding or the label — and a box to add to it. */
export function Remarks(props: {
  runId: number
  mid: string
  target: RemarkTarget
  role: Role | null
  compact?: boolean
}) {
  const { runId, mid, target } = props
  const remarks = useQuery(evalRemarksQuery(runId, mid))
  const post = usePostRemarkMutation(runId, mid)
  const [draft, setDraft] = React.useState("")
  const inputId = `eval-remark-${target.kind}-${target.ref || "label"}`
  const thread = (remarks.data ?? []).filter(
    (r) => r.targetKind === target.kind && r.targetRef === target.ref,
  )

  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    const body = draft.trim()
    if (!body) return
    post.mutate(
      { targetKind: target.kind, targetRef: target.ref, body },
      {
        onSuccess: () => {
          setDraft("")
        },
      },
    )
  }

  return (
    <div className={props.compact ? "" : "rounded-md border border-border bg-surface p-4"}>
      <h3 className="mb-3 flex items-center gap-1.5 text-body-sm font-semibold text-text">
        <MessageSquare className="size-4" aria-hidden /> Remarks on {target.title}
      </h3>
      {remarks.isPending && <SkeletonText lines={2} />}
      {remarks.isError && (
        <EmptyState
          reason="error"
          bare
          title="The remarks could not be loaded"
          body={remarks.error.message}
        />
      )}
      {remarks.isSuccess && thread.length === 0 && (
        <p className="text-body-sm text-text-3">No remarks yet.</p>
      )}
      <ul className="space-y-2.5">
        {thread.map((r) => (
          <RemarkItem key={r.id} remark={r} />
        ))}
      </ul>
      {canRemarkOnEvalRun(props.role) && (
        <form onSubmit={submit} className="mt-3 flex flex-col gap-2">
          <label htmlFor={inputId} className="sr-only">
            Remark on {target.title}
          </label>
          <Textarea
            id={inputId}
            minRows={props.compact ? 2 : 3}
            value={draft}
            maxLength={5000}
            onChange={(e) => {
              setDraft(e.target.value)
            }}
            placeholder="What should the review have said here?"
          />
          {post.isError && <p className="text-caption text-err">The remark could not be saved.</p>}
          <Button
            type="submit"
            size="sm"
            className="self-end"
            disabled={!draft.trim()}
            loading={post.isPending}
          >
            Add remark
          </Button>
        </form>
      )}
    </div>
  )
}

function RemarkItem({ remark }: { remark: Remark }) {
  return (
    <li className="rounded-md bg-surface-sunken px-3 py-2 text-body-sm text-text">
      <div className="mb-0.5 flex flex-wrap items-baseline gap-x-1.5 text-caption text-text-3">
        <span className="font-medium text-text-2">{remark.authorName ?? remark.authorRole}</span>
        {remark.authorName && <span>{remark.authorRole}</span>}
        <span aria-hidden>·</span>
        <span>{formatRelative(remark.createdAt)}</span>
      </div>
      <p className="whitespace-pre-wrap">{remark.body}</p>
    </li>
  )
}

/** The thread a selected mark or finding remarks into, or null when nothing is selected. */
export function remarkTargetOf(
  mark: EvalMark | undefined,
  finding: EvalFinding | undefined,
): RemarkTarget | null {
  if (mark) return { kind: "mark", ref: mark.id, title: `mark ${mark.id}` }
  if (finding) {
    return { kind: "finding", ref: String(finding.id), title: `finding ${String(finding.id)}` }
  }
  return null
}

/** The card that opens on the page beside a selected box: what it is, and its remarks. */
export function BoxCard(props: {
  runId: number
  mid: string
  role: Role | null
  mark: EvalMark | undefined
  finding: EvalFinding | undefined
  onClose: () => void
}) {
  const { mark, finding } = props
  const target = remarkTargetOf(mark, finding)
  if (!target) return null
  return (
    <div
      role="dialog"
      aria-label={`Remarks on ${target.title}`}
      className="max-h-[340px] overflow-y-auto rounded-md border border-border bg-surface p-3 shadow-lg"
    >
      <div className="mb-1 flex items-start gap-2">
        {mark ? (
          <span
            className="rounded-full px-2 py-0.5 text-caption font-semibold text-white"
            style={{ background: OUTCOME_TONE[mark.outcome] }}
          >
            {OUTCOME_LABEL[mark.outcome]}
          </span>
        ) : (
          <span className="rounded-full border border-border px-2 py-0.5 text-caption text-text-2">
            {finding?.ruleId}
          </span>
        )}
        <span className="flex-1 text-caption text-text-3">
          {mark ? `Expert mark ${mark.id}` : `Our finding ${String(finding?.id)}`}
        </span>
        <IconButton label="Close" variant="ghost" icon={<X />} onClick={props.onClose} noTooltip />
      </div>
      <p className="mb-2 line-clamp-3 text-body-sm text-text">
        {mark ? mark.comment : finding?.message}
      </p>
      {/* Keyed by the target: the card stays mounted as the selection moves between boxes, and a
          draft typed on one must not carry over to — and post on — the next. */}
      <Remarks
        key={`${target.kind}:${target.ref}`}
        runId={props.runId}
        mid={props.mid}
        target={target}
        role={props.role}
        compact
      />
    </div>
  )
}
